<template>
  <main-section
    :isEditMode="isEditMode"
    :isDiff="service.isDiff"
    :isOutdated="isOutdated"
    :item="service"
    :itemType="'MQ_SERVICE'"
    :typename="'service'"
    :onUpdate="updateService"
    :onCreate="createService"
    :onDelete="deleteService"
    @change-chosen="onChangeChosen"
    @revert="onRevert"
  >
    <fieldset slot="fieldset" :disabled="isOutdated || service.isDiff">
      <div class="two fields" v-diff-field="serviceDiff.name">
        <div class="field required" :class="{ error: $v.service.name.$error }">
          <label>{{ $t('SERVICE_NAME') }}</label>
          <input
            type="text"
            name="serviceName"
            :placeholder="$t('SERVICE_NAME_PLACEHOLDER')"
            v-model="service.name"
            @click="$v.service.name.$touch()"
            :disabled="isOutdated || service.isDiff"
          />
        </div>
        <div
          class="field required"
          :class="{ error: $v.service.key.$error }"
          v-diff-field="serviceDiff.key"
        >
          <label>{{ $t('SERVICE_KEY') }}</label>
          <input
            type="text"
            name="serviceKey"
            :placeholder="$t('SERVICE_KEY_PLACEHOLDER')"
            v-model="service.key"
            @click="$v.service.key.$touch()"
            :disabled="isOutdated || service.isDiff"
          />
        </div>
      </div>
      <h4 class="ui horizontal left aligned divider header">{{ $t('MQ_SETTINGS') }}</h4>
      <div class="ui basic segment">
        <div class="ui tiny negative message" v-if="errorMessage">
          <i class="bullhorn icon"></i>
          {{ errorMessage }}
        </div>
        <div class="equal width fields">
          <!-- HOST -->
          <div
            class="field required"
            :class="{ error: $v.service.serviceConfiguration.host.$error }"
            v-diff-field="serviceDiff.serviceConfiguration.host"
          >
            <label for="mq-host">{{ $t('MQ_CONFIG_HOST') }}</label>
            <input
              id="mq-host"
              class="monospaced"
              type="text"
              v-model="service.serviceConfiguration.host"
              :disabled="isOutdated || service.isDiff"
            />
          </div>
          <!-- PORT -->
          <div
            class="field required"
            :class="{ error: $v.service.serviceConfiguration.port.$error }"
            v-diff-field="serviceDiff.serviceConfiguration.port"
          >
            <label for="mq-port">{{ $t('MQ_CONFIG_PORT') }}</label>
            <input
              id="mq-port"
              class="monospaced"
              type="number"
              v-model="service.serviceConfiguration.port"
              :disabled="isOutdated || service.isDiff"
            />
          </div>
        </div>
        <div class="equal width fields">
          <!-- USER -->
          <div class="field" v-diff-field="serviceDiff.serviceConfiguration.user">
            <label for="mq-user">{{ $t('MQ_CONFIG_USER') }}</label>
            <input
              id="mq-user"
              class="monospaced"
              type="text"
              autocomplete="off"
              v-model="service.serviceConfiguration.user"
              :disabled="isOutdated || service.isDiff"
            />
          </div>
          <!-- PASS -->
          <div class="field" v-diff-field="serviceDiff.serviceConfiguration.pass">
            <label for="mq-pass">{{ $t('MQ_CONFIG_PASS') }}</label>
            <password-input
              id="mq-pass"
              type="password"
              v-model="service.serviceConfiguration.pass"
              :disabled="isOutdated || service.isDiff"
            />
          </div>
        </div>
        <div class="equal width fields">
          <!-- VIRTUAL HOST -->
          <div class="field" v-diff-field="serviceDiff.serviceConfiguration.virtualHost">
            <label for="mq-vhost">{{ $t('MQ_CONFIG_VHOST') }}</label>
            <input
              id="mq-vhost"
              class="monospaced"
              type="text"
              v-model="service.serviceConfiguration.virtualHost"
              :disabled="isOutdated || service.isDiff"
            />
          </div>
          <!-- EXCHANGE -->
          <div class="field" v-diff-field="serviceDiff.serviceConfiguration.exchange">
            <label for="mq-exchange">{{ $t('MQ_CONFIG_EXCHANGE') }}</label>
            <input
              id="mq-exchange"
              class="monospaced"
              type="text"
              v-model="service.serviceConfiguration.exchange"
              :disabled="isOutdated || service.isDiff"
            />
          </div>
        </div>
        <div class="equal width fields">
          <!-- QUEUE NAME -->
          <div class="field" v-diff-field="serviceDiff.serviceConfiguration.queueName">
            <label for="mq-queueName">{{ $t('MQ_CONFIG_QUEUE_NAME') }}</label>
            <input
              id="mq-queueName"
              class="monospaced"
              type="text"
              v-model="service.serviceConfiguration.queueName"
              :disabled="isOutdated || service.isDiff"
            />
          </div>
          <!-- EXCHANGE -->
          <div class="field" v-diff-field="serviceDiff.serviceConfiguration.routingKeyRegex">
            <label for="mq-routingKeyRegex">{{ $t('MQ_CONFIG_ROUTING_KEY') }}</label>
            <input
              id="mq-routingKeyRegex"
              class="monospaced"
              type="text"
              v-model="service.serviceConfiguration.routingKeyRegex"
              :disabled="isOutdated || service.isDiff"
            />
          </div>
        </div>
        <!-- ENCODING -->
        <div class="field" v-diff-field="serviceDiff.serviceConfiguration.encoding">
          <typeahead
            :disabled="isOutdated || service.isDiff"
            :items="suggestedEncodings"
            :current-item="display(suggestedEncodings, service.serviceConfiguration.encoding)"
            :label-text="$tc('MQ_CONFIG_ENCODING', 1)"
            :placeholder-text="$tc('MQ_CONFIG_ENCODING', 1)"
            :select-handler="encodingHandler.selection"
            :remove-handler="encodingHandler.removal"
            :create-handler="encodingHandler.creation"
          ></typeahead>
        </div>
      </div>

      <!--
        required field:
        <div class="field required" :class="{error : $v.service.serviceConfiguration.portPattern.$error}" v-diff-field="serviceDiff.serviceConfiguration.portPattern" >
           <label for="port-pattern">{{ $t('SERIALPORT_PORT_PATTERN') }}</label>
           <input
             id="port-pattern"
             class="monospaced"
             type="text"
             v-model="service.serviceConfiguration.portPattern"
             :disabled="isOutdated || service.isDiff"
         </div>
      -->
      <!-- Used in ... -->
      <template v-if="mode === 'edit'">
        <h4 class="ui horizontal left aligned divider header">
          {{ $t('USED_IN', { thing: $tc('FLOW', (usedIn && usedIn.length) || 0) }) }}
        </h4>
        <div class="ui basic segment">
          <div class="field" v-if="usedIn && usedIn.length > 0">
            <div class="ui link list" id="usedIn">
              <router-link
                class="item"
                :to="{ name: u.routeName, params: u.routeParams }"
                :key="u.id"
                v-for="u in usedIn"
              >
                <span>
                  <i class="share icon" v-bind:class="{ hidden: u.as === 'trigger' }" />
                  <span class="content">{{ u.name }}</span>
                </span>
              </router-link>
            </div>
          </div>
          <div v-else>
            <span class="text bold">{{ $t('SERVICE_NOT_USED') }}</span>
          </div>
        </div>
      </template>
    </fieldset>
  </main-section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';
</style>

<script>
// @flow
import Vue from 'vue'
import _ from 'lodash'
import { Service, Flow, Toast, ErrorCodes } from 'services'
import { required, numeric, helpers, maxValue } from 'vuelidate/lib/validators'
import { MainSection, Typeahead, PasswordInput } from 'ui'
import { map } from 'rxjs/operators'

const blankserviceConfiguration = {
  host: '',
  port: 5672,
  user: '',
  pass: '',
  virtualHost: '',
  exchange: '',
  queueName: '',
  routingKeyRegex: '',
  encoding: 'utf-8'
}

function SelectionHandler(setFn, allowCreate) {
  this.selection = function(newVal) {
    setFn(newVal.id)
  }
  this.removal = function() {
    setFn('')
  }
  this.creation = function(newName) {
    if (allowCreate) {
      setFn(newName)
    }
  }
}

export default {
  name: 'mq-service',
  props: ['serviceId'],
  components: { MainSection, Typeahead, PasswordInput },

  data() {
    return {
      service: {
        serviceType: 'MQ',
        serviceConfiguration: _.clone(blankserviceConfiguration)
      },
      isOutdated: false,
      errorMessage: null,
      serviceDiff: { serviceConfiguration: {} },
      suggestedEncodings: [
        { id: 'IBM037', name: 'IBM EBCDIC (US-Canada)' },
        { id: 'IBM437', name: 'OEM United States' },
        { id: 'IBM500', name: 'IBM EBCDIC (International)' },
        { id: 'ASMO-708', name: 'Arabic (ASMO 708)' },
        { id: 'DOS-720', name: 'Arabic (DOS)' },
        { id: 'ibm737', name: 'Greek (DOS)' },
        { id: 'ibm775', name: 'Baltic (DOS)' },
        { id: 'ibm850', name: 'Western European (DOS)' },
        { id: 'ibm852', name: 'Central European (DOS)' },
        { id: 'IBM855', name: 'OEM Cyrillic' },
        { id: 'ibm857', name: 'Turkish (DOS)' },
        { id: 'IBM00858', name: 'OEM Multilingual Latin I' },
        { id: 'IBM860', name: 'Portuguese (DOS)' },
        { id: 'ibm861', name: 'Icelandic (DOS)' },
        { id: 'DOS-862', name: 'Hebrew (DOS)' },
        { id: 'IBM863', name: 'French Canadian (DOS)' },
        { id: 'IBM864', name: 'Arabic (864)' },
        { id: 'IBM865', name: 'Nordic (DOS)' },
        { id: 'cp866', name: 'Cyrillic (DOS)' },
        { id: 'ibm869', name: 'Greek, Modern (DOS)' },
        { id: 'IBM870', name: 'IBM EBCDIC (Multilingual Latin-2)' },
        { id: 'windows-874', name: 'Thai (Windows)' },
        { id: 'cp875', name: 'IBM EBCDIC (Greek Modern)' },
        { id: 'shift_jis', name: 'Japanese (Shift-JIS)' },
        { id: 'gb2312', name: 'Chinese Simplified (GB2312)' },
        { id: 'ks_c_5601-1987', name: 'Korean' },
        { id: 'big5', name: 'Chinese Traditional (Big5)' },
        { id: 'IBM1026', name: 'IBM EBCDIC (Turkish Latin-5)' },
        { id: 'IBM01047', name: 'IBM Latin-1' },
        { id: 'IBM01140', name: 'IBM EBCDIC (US-Canada-Euro)' },
        { id: 'IBM01141', name: 'IBM EBCDIC (Germany-Euro)' },
        { id: 'IBM01142', name: 'IBM EBCDIC (Denmark-Norway-Euro)' },
        { id: 'IBM01143', name: 'IBM EBCDIC (Finland-Sweden-Euro)' },
        { id: 'IBM01144', name: 'IBM EBCDIC (Italy-Euro)' },
        { id: 'IBM01145', name: 'IBM EBCDIC (Spain-Euro)' },
        { id: 'IBM01146', name: 'IBM EBCDIC (UK-Euro)' },
        { id: 'IBM01147', name: 'IBM EBCDIC (France-Euro)' },
        { id: 'IBM01148', name: 'IBM EBCDIC (International-Euro)' },
        { id: 'IBM01149', name: 'IBM EBCDIC (Icelandic-Euro)' },
        { id: 'utf-16', name: 'Unicode' },
        { id: 'unicodeFFFE', name: 'Unicode (Big endian)' },
        { id: 'windows-1250', name: 'Central European (Windows)' },
        { id: 'windows-1251', name: 'Cyrillic (Windows)' },
        { id: 'Windows-1252', name: 'Western European (Windows)' },
        { id: 'windows-1253', name: 'Greek (Windows)' },
        { id: 'windows-1254', name: 'Turkish (Windows)' },
        { id: 'windows-1255', name: 'Hebrew (Windows)' },
        { id: 'windows-1256', name: 'Arabic (Windows)' },
        { id: 'windows-1257', name: 'Baltic (Windows)' },
        { id: 'windows-1258', name: 'Vietnamese (Windows)' },
        { id: 'Johab', name: 'Korean (Johab)' },
        { id: 'macintosh', name: 'Western European (Mac)' },
        { id: 'x-mac-japanese', name: 'Japanese (Mac)' },
        { id: 'x-mac-chinesetrad', name: 'Chinese Traditional (Mac)' },
        { id: 'x-mac-korean', name: 'Korean (Mac)' },
        { id: 'x-mac-arabic', name: 'Arabic (Mac)' },
        { id: 'x-mac-hebrew', name: 'Hebrew (Mac)' },
        { id: 'x-mac-greek', name: 'Greek (Mac)' },
        { id: 'x-mac-cyrillic', name: 'Cyrillic (Mac)' },
        { id: 'x-mac-chinesesimp', name: 'Chinese Simplified (Mac)' },
        { id: 'x-mac-romanian', name: 'Romanian (Mac)' },
        { id: 'x-mac-ukrainian', name: 'Ukrainian (Mac)' },
        { id: 'x-mac-thai', name: 'Thai (Mac)' },
        { id: 'x-mac-ce', name: 'Central European (Mac)' },
        { id: 'x-mac-icelandic', name: 'Icelandic (Mac)' },
        { id: 'x-mac-turkish', name: 'Turkish (Mac)' },
        { id: 'x-mac-croatian', name: 'Croatian (Mac)' },
        { id: 'utf-32', name: 'Unicode (UTF-32)' },
        { id: 'utf-32BE', name: 'Unicode (UTF-32 Big endian)' },
        { id: 'x-Chinese-CNS', name: 'Chinese Traditional (CNS)' },
        { id: 'x-cp20001', name: 'TCA Taiwan' },
        { id: 'x-Chinese-Eten', name: 'Chinese Traditional (Eten)' },
        { id: 'x-cp20003', name: 'IBM5550 Taiwan' },
        { id: 'x-cp20004', name: 'TeleText Taiwan' },
        { id: 'x-cp20005', name: 'Wang Taiwan' },
        { id: 'x-IA5', name: 'Western European (IA5)' },
        { id: 'x-IA5-German', name: 'German (IA5)' },
        { id: 'x-IA5-Swedish', name: 'Swedish (IA5)' },
        { id: 'x-IA5-Norwegian', name: 'Norwegian (IA5)' },
        { id: 'us-ascii', name: 'US-ASCII' },
        { id: 'x-cp20261', name: 'T.61' },
        { id: 'x-cp20269', name: 'ISO-6937' },
        { id: 'IBM273', name: 'IBM EBCDIC (Germany)' },
        { id: 'IBM277', name: 'IBM EBCDIC (Denmark-Norway)' },
        { id: 'IBM278', name: 'IBM EBCDIC (Finland-Sweden)' },
        { id: 'IBM280', name: 'IBM EBCDIC (Italy)' },
        { id: 'IBM284', name: 'IBM EBCDIC (Spain)' },
        { id: 'IBM285', name: 'IBM EBCDIC (UK)' },
        { id: 'IBM290', name: 'IBM EBCDIC (Japanese katakana)' },
        { id: 'IBM297', name: 'IBM EBCDIC (France)' },
        { id: 'IBM420', name: 'IBM EBCDIC (Arabic)' },
        { id: 'IBM423', name: 'IBM EBCDIC (Greek)' },
        { id: 'IBM424', name: 'IBM EBCDIC (Hebrew)' },
        { id: 'x-EBCDIC-KoreanExtended', name: 'IBM EBCDIC (Korean Extended)' },
        { id: 'IBM-Thai', name: 'IBM EBCDIC (Thai)' },
        { id: 'koi8-r', name: 'Cyrillic (KOI8-R)' },
        { id: 'IBM871', name: 'IBM EBCDIC (Icelandic)' },
        { id: 'IBM880', name: 'IBM EBCDIC (Cyrillic Russian)' },
        { id: 'IBM905', name: 'IBM EBCDIC (Turkish)' },
        { id: 'IBM00924', name: 'IBM Latin-1' },
        { id: 'EUC-JP', name: 'Japanese (JIS 0208-1990 and 0212-1990)' },
        { id: 'x-cp20936', name: 'Chinese Simplified (GB2312-80)' },
        { id: 'x-cp20949', name: 'Korean Wansung' },
        { id: 'cp1025', name: 'IBM EBCDIC (Cyrillic Serbian-Bulgarian)' },
        { id: 'koi8-u', name: 'Cyrillic (KOI8-U)' },
        { id: 'iso-8859-1', name: 'Western European (ISO)' },
        { id: 'iso-8859-2', name: 'Central European (ISO)' },
        { id: 'iso-8859-3', name: 'Latin 3 (ISO)' },
        { id: 'iso-8859-4', name: 'Baltic (ISO)' },
        { id: 'iso-8859-5', name: 'Cyrillic (ISO)' },
        { id: 'iso-8859-6', name: 'Arabic (ISO)' },
        { id: 'iso-8859-7', name: 'Greek (ISO)' },
        { id: 'iso-8859-8', name: 'Hebrew (ISO-Visual)' },
        { id: 'iso-8859-9', name: 'Turkish (ISO)' },
        { id: 'iso-8859-13', name: 'Estonian (ISO)' },
        { id: 'iso-8859-15', name: 'Latin 9 (ISO)' },
        { id: 'x-Europa', name: 'Europa' },
        { id: 'iso-8859-8-i', name: 'Hebrew (ISO-Logical)' },
        { id: 'iso-2022-jp', name: 'Japanese (JIS)' },
        { id: 'csISO2022JP', name: 'Japanese (JIS-Allow 1 byte Kana)' },
        { id: 'iso-2022-jp', name: 'Japanese (JIS-Allow 1 byte Kana - SO/SI)' },
        { id: 'iso-2022-kr', name: 'Korean (ISO)' },
        { id: 'x-cp50227', name: 'Chinese Simplified (ISO-2022)' },
        { id: 'euc-jp', name: 'Japanese (EUC)' },
        { id: 'EUC-CN', name: 'Chinese Simplified (EUC)' },
        { id: 'euc-kr', name: 'Korean (EUC)' },
        { id: 'hz-gb-2312', name: 'Chinese Simplified (HZ)' },
        { id: 'GB18030', name: 'Chinese Simplified (GB18030)' },
        { id: 'x-iscii-de', name: 'ISCII Devanagari' },
        { id: 'x-iscii-be', name: 'ISCII Bengali' },
        { id: 'x-iscii-ta', name: 'ISCII Tamil' },
        { id: 'x-iscii-te', name: 'ISCII Telugu' },
        { id: 'x-iscii-as', name: 'ISCII Assamese' },
        { id: 'x-iscii-or', name: 'ISCII Oriya' },
        { id: 'x-iscii-ka', name: 'ISCII Kannada' },
        { id: 'x-iscii-ma', name: 'ISCII Malayalam' },
        { id: 'x-iscii-gu', name: 'ISCII Gujarati' },
        { id: 'x-iscii-pa', name: 'ISCII Punjabi' },
        { id: 'utf-7', name: 'Unicode (UTF-7)' },
        { id: 'utf-8', name: 'Unicode (UTF-8)' }
      ].map(e => {
        return { id: e.id, name: `${e.name} (${e.id})` }
      }),
      encodingHandler: new SelectionHandler(val => {
        this.service.serviceConfiguration.encoding = val
      })
    }
  },

  validations: {
    service: {
      name: { required },
      key: { required },
      serviceConfiguration: {
        host: { required },
        port: { required }
      }
    }
  },

  computed: {
    allFlows() {
      return this.$store.state.flow.items
    },
    mode() {
      return this.service && this.service.id ? 'edit' : 'create'
    },

    usedIn() {
      return _.map(
        _.filter(
          this.allFlows,
          f =>
            f.typeConfig &&
            f.typeConfig.triggers &&
            f.typeConfig.triggers.services &&
            _.find(
              f.typeConfig.triggers.services,
              s =>
                s.serviceType === 'MQ' &&
                this.service &&
                s.configuration.serviceId === this.service.id
            )
        ),
        f => {
          return {
            as: 'trigger',
            name: f.name,
            routeName: 'app-flow',
            routeParams: {
              appId: f.application,
              flowId: f.id,
              mode: 'settings'
            }
          }
        }
      )
    },
    isEditMode() {
      return this.mode === 'edit'
    }
  },

  methods: {
    display(items, id) {
      const item = _.find(items, i => i.id === id)
      return (item && item.name) || id
    },
    updateService() {
      if (!this.$v.$invalid) {
        if (!this.isOutdated) {
          return Service.updateService(this.serviceId, this.service)
            .then(res => {
              this.service.version = res.version
              Toast.success(this.$i18n.t('UPDATED', { thing: this.$i18n.tc('SERVICE', 1) }), {
                remove: 5000
              })
            })
            .catch(err => {
              if (err.code === ErrorCodes.ITEM_OUTDATED) {
                this.isOutdated = true
                Toast.error(
                  this.$i18n.t('ERR_OUTDATED_VERSION', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SERVICE', 1) }),
                    thing: this.$i18n.tc('SERVICE', 1)
                  })
                )
              } else {
                Toast.error(
                  this.$i18n.t('ERR', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SERVICE', 1) })
                  })
                )
                console.error(err)
              }
              throw err
            })
        } else {
          Toast.error(
            this.$i18n.t('ERR_OUTDATED_VERSION', {
              action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SERVICE', 1) }),
              thing: this.$i18n.tc('SERVICE', 1)
            })
          )
          return Promise.reject('Outdated version')
        }
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SERVICE', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    createService() {
      if (!this.$v.$invalid) {
        return Service.createService(this.service)
          .then(res => {
            this.service.id = res.id
            this.service.version = res.version
          })
          .then(() => {
            Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('SERVICE', 1) }), {
              remove: 5000
            })
            this.$router.push({
              name: 'service',
              params: { serviceId: this.service.id, serviceType: 'MQ' }
            })
          })
          .catch(err => {
            Toast.error(
              this.$i18n.t('ERR', {
                action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('SERVICE', 1) })
              }),
              { remove: 5000 }
            )
            console.error(err)
          })
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('SERVICE', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    deleteService() {
      Service.deleteService(this.service.id)
        .then(() => {
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('SERVICE', 1) }), {
            remove: 5000
          })
          this.$router.push({
            name: 'services'
          })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('DELETE', { thing: this.$i18n.tc('SERVICE', 1) })
            })
          )
          console.error(err)
        })
    },

    readService(id) {
      return Service.readService(id)
        .then(s => {
          this.service = _.merge(
            { serviceConfiguration: _.clone(blankserviceConfiguration) },
            s || {}
          )
          this.isOutdated = false
          Vue.nextTick(this.$v.$touch)
          return this.service
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('READ', { thing: this.$i18n.tc('SERVICE', 1) })
            })
          )
          console.error(err)
        })
    },

    editMode() {
      this.$v.$reset()
      this.readService(this.$route.params.serviceId)
    },

    createMode() {
      this.$v.$reset()
      this.service = {
        serviceType: 'MQ',
        serviceConfiguration: _.clone(blankserviceConfiguration)
      }
    },
    onChangeChosen({ changedItem, fullDiff }) {
      this.service = changedItem
      this.serviceDiff = !_.isEmpty(fullDiff) ? fullDiff : { serviceConfiguration: {} }
    },
    onRevert(service) {
      this.service = service
      this.serviceDiff = { serviceConfiguration: {} }
    }
  },

  created() {
    if (this.$route.params.serviceId) {
      this.$v.$reset()
      this.readService(this.$route.params.serviceId)
    }
    Flow.readAllFlows()
  },

  watch: {
    '$route.params.serviceId': function(newServiceId) {
      if (newServiceId) {
        this.editMode()
      } else {
        this.createMode()
      }
    }
  }
}
//
</script>
